/**
 * Typed admin-API client.
 *
 * Reads the API base URL from `import.meta.env.VITE_API_URL`. In production
 * the SPA is served from `admin.rallycomm.com` and Cloudflare Access auto-
 * injects the JWT on every request — we don't need to forward anything.
 *
 * In local dev (`VITE_API_URL` pointing at `http://localhost:8787`) the API
 * has no Access policy in front of it, so we send `X-Dev-Admin: 1`. The API
 * accepts that header ONLY when its own `CF_ACCESS_*` env vars are empty —
 * see `backend/api/src/handlers/_admin.ts`. This means a misconfigured
 * production API will reject the dev header even if the SPA tries to send it.
 */

import type {
  AddZelloChannelInputs,
  AddZelloDeviceInputs,
  AdminHealthResponse,
  AuditLogListResponse,
  ComplimentarySubscriptionCreateInputs,
  Customer,
  CustomerCreateInputs,
  CustomerPatch,
  CustomerZelloState,
  Device,
  DevicePatch,
  DevicesListFilter,
  FleetHealthCounts,
  Heartbeat,
  OtaInstallLog,
  OtaRelease,
  PendingSimAction,
  ReleasePatch,
  ReleaseUploadInputs,
  RallyStreamDevice,
  RallyStreamDeviceCreateInputs,
  SimUsage,
  Subscription,
  SubscriptionCreateInputs,
  ZelloChannel,
  ZelloUser,
} from "./types";

const RAW_BASE = (import.meta.env.VITE_API_URL ?? "").trim();
export const API_BASE = RAW_BASE.replace(/\/+$/, "");

/**
 * True when the API base URL points at localhost — i.e. we're talking to a
 * `wrangler dev` instance. The dev-admin header is only attached in this
 * case so deployed builds can never accidentally send it.
 */
export const IS_DEV_API = (() => {
  if (!API_BASE) return false;
  try {
    const u = new URL(API_BASE);
    return u.hostname === "localhost" || u.hostname === "127.0.0.1";
  } catch {
    return false;
  }
})();

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly reason: string,
    readonly detail?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

interface ApiErrorBody {
  ok: false;
  error: string;
  detail?: unknown;
}

async function request<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  if (!API_BASE) {
    throw new ApiError(
      "VITE_API_URL is not set. Copy backend/admin/.env.example to .env.local.",
      0,
      "no_api_base",
    );
  }

  const url = `${API_BASE}${path}`;
  const headers = new Headers(init.headers);
  if (init.body !== undefined && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  headers.set("Accept", "application/json");
  if (IS_DEV_API) {
    headers.set("X-Dev-Admin", "1");
  }

  const res = await fetch(url, {
    ...init,
    headers,
    // `include` so Cloudflare Access cookies travel with the request in prod.
    // The dev API doesn't set cookies, but `include` is harmless when there
    // are none to send.
    credentials: "include",
  });

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let parsed: unknown;
  try {
    parsed = text ? JSON.parse(text) : undefined;
  } catch {
    if (!res.ok) {
      throw new ApiError(`HTTP ${res.status}: ${text || res.statusText}`, res.status, "non_json");
    }
    return undefined as T;
  }

  if (!res.ok) {
    const body = parsed as ApiErrorBody | undefined;
    const reason = body?.error ?? `http_${res.status}`;
    throw new ApiError(
      `HTTP ${res.status}: ${reason}`,
      res.status,
      reason,
      body?.detail,
    );
  }
  return parsed as T;
}

// ---------------------------------------------------------------------------
// Devices
// ---------------------------------------------------------------------------

export async function listDevices(filter: DevicesListFilter = {}): Promise<Device[]> {
  const params = new URLSearchParams();
  if (filter.channel) params.set("channel", filter.channel);
  if (filter.customer) params.set("customer", filter.customer);
  if (filter.health) {
    params.set("health", filter.health);
  } else if (filter.online) {
    params.set("online", "1");
  }
  const qs = params.toString();
  const data = await request<{ devices: Device[] }>(
    `/api/admin/devices${qs ? `?${qs}` : ""}`,
  );
  return data.devices ?? [];
}

export async function getDevice(serial: string): Promise<Device> {
  const data = await request<{ device: Device }>(
    `/api/admin/devices/${encodeURIComponent(serial)}`,
  );
  return data.device;
}

export async function patchDevice(
  serial: string,
  patch: DevicePatch,
): Promise<Device> {
  const data = await request<{ device: Device }>(
    `/api/admin/devices/${encodeURIComponent(serial)}`,
    {
      method: "PATCH",
      body: JSON.stringify(patch),
    },
  );
  return data.device;
}

/**
 * Per-subscription teardown report returned by /unlink-customer. One
 * entry per active subscription that was found for the device's prior
 * customer; `ok=false` means the cancel call failed and the operator
 * should investigate (e.g. via the Stripe dashboard).
 */
export interface UnlinkSubscriptionResult {
  stripe_subscription_id: string;
  status: string;
  complimentary: boolean;
  ok: boolean;
  error?: string;
}

/**
 * Response from POST /api/admin/devices/:serial/unlink-customer.
 *
 * Each peripheral system reports its own status string so the operator
 * gets a clear "what actually happened" recap without having to go
 * dig in audit logs:
 *
 *   hologram — "paused" | "paused_already" | "no_sim"
 *              | "skipped:hologram_unconfigured" | "failed:<msg>"
 *   twilio   — "released" | "released_already" | "no_number"
 *              | "skipped:twilio_unconfigured" | "failed:<msg>"
 */
export interface UnlinkResult {
  ok: boolean;
  device_serial: string;
  previous_customer_id: string | null;
  subscriptions: UnlinkSubscriptionResult[];
  hologram: string;
  twilio: string;
  pending_pauses_cancelled: number;
}

/**
 * Operator-driven full decommission of a device's customer link. See
 * `backend/api/src/handlers/admin_unlink.ts` for the full sequencing
 * and idempotency invariants.
 *
 * Side effects (one shot, cannot be undone individually):
 *   - cancels any active Stripe (or complimentary) subscription
 *     attached to THIS device under the customer
 *   - pauses the device's Hologram SIM (best-effort)
 *   - PERMANENTLY releases the device's Twilio number back to the
 *     pool (best-effort; Twilio rarely reissues released numbers)
 *   - clears device.customer_id, subscription_active, twilio_phone_*
 *
 * The returned `UnlinkResult` carries per-step outcomes so the UI can
 * summarise what succeeded vs. what needs operator follow-up.
 */
export async function unlinkCustomerFromDevice(
  serial: string,
): Promise<UnlinkResult> {
  return request<UnlinkResult>(
    `/api/admin/devices/${encodeURIComponent(serial)}/unlink-customer`,
    { method: "POST" },
  );
}

export async function listDeviceHeartbeats(
  serial: string,
  limit = 50,
  includeJournal = false,
): Promise<Heartbeat[]> {
  const q = new URLSearchParams();
  q.set("limit", String(limit));
  if (includeJournal) q.set("include_journal", "true");
  const data = await request<{ heartbeats: Heartbeat[] }>(
    `/api/admin/devices/${encodeURIComponent(serial)}/heartbeats?${q.toString()}`,
  );
  return data.heartbeats ?? [];
}

export async function getFleetHealth(): Promise<FleetHealthCounts> {
  const data = await request<FleetHealthCounts & { ok: boolean }>(
    `/api/admin/fleet/health`,
  );
  return {
    total: data.total,
    online: data.online,
    stale: data.stale,
    offline: data.offline,
  };
}

export async function getAdminHealth(): Promise<AdminHealthResponse> {
  return request<AdminHealthResponse>(`/api/admin/health`);
}

export async function listAuditLog(params: {
  operator?: string;
  action?: string;
  target?: string;
  since?: string;
  until?: string;
  page?: number;
  limit?: number;
}): Promise<AuditLogListResponse> {
  const q = new URLSearchParams();
  if (params.operator) q.set("operator", params.operator);
  if (params.action) q.set("action", params.action);
  if (params.target) q.set("target", params.target);
  if (params.since) q.set("since", params.since);
  if (params.until) q.set("until", params.until);
  if (params.page) q.set("page", String(params.page));
  if (params.limit) q.set("limit", String(params.limit));
  const qs = q.toString();
  return request<AuditLogListResponse>(
    `/api/admin/audit${qs ? `?${qs}` : ""}`,
  );
}

// ---------------------------------------------------------------------------
// Customers
// ---------------------------------------------------------------------------

export async function listCustomers(): Promise<Customer[]> {
  const data = await request<{ customers: Customer[] }>(`/api/admin/customers`);
  return data.customers ?? [];
}

// ---------------------------------------------------------------------------
// OTA releases — Phase 3
// ---------------------------------------------------------------------------

export async function listReleases(channel?: string): Promise<OtaRelease[]> {
  const qs = channel ? `?channel=${encodeURIComponent(channel)}` : "";
  const data = await request<{ releases: OtaRelease[] }>(
    `/api/admin/releases${qs}`,
  );
  return data.releases ?? [];
}

/**
 * Upload a signed firmware tarball.
 *
 * The form is multipart/form-data — `request()` only handles JSON, so we
 * call `fetch` directly here and replicate its auth + error handling.
 * Setting `Content-Type` manually would break the boundary that
 * `FormData` injects, so we let the browser pick it.
 */
export async function uploadRelease(
  inputs: ReleaseUploadInputs,
): Promise<OtaRelease> {
  if (!API_BASE) {
    throw new ApiError(
      "VITE_API_URL is not set. Copy backend/admin/.env.example to .env.local.",
      0,
      "no_api_base",
    );
  }
  const form = new FormData();
  form.set("file", inputs.file);
  form.set("version", inputs.version);
  form.set("channel", inputs.channel);
  form.set("signature", inputs.signature);
  if (inputs.notes) form.set("notes", inputs.notes);
  if (inputs.min_from_version) {
    form.set("min_from_version", inputs.min_from_version);
  }

  const headers = new Headers();
  headers.set("Accept", "application/json");
  if (IS_DEV_API) headers.set("X-Dev-Admin", "1");

  const res = await fetch(`${API_BASE}/api/admin/releases`, {
    method: "POST",
    body: form,
    headers,
    credentials: "include",
  });
  const text = await res.text();
  let parsed: unknown;
  try {
    parsed = text ? JSON.parse(text) : undefined;
  } catch {
    if (!res.ok) {
      throw new ApiError(`HTTP ${res.status}: ${text || res.statusText}`, res.status, "non_json");
    }
    throw new ApiError("Empty response", res.status, "empty_response");
  }
  if (!res.ok) {
    const body = parsed as { error?: string; detail?: unknown } | undefined;
    const reason = body?.error ?? `http_${res.status}`;
    throw new ApiError(`HTTP ${res.status}: ${reason}`, res.status, reason, body?.detail);
  }
  const data = parsed as { release: OtaRelease };
  return data.release;
}

export async function patchRelease(
  version: string,
  patch: ReleasePatch,
): Promise<OtaRelease> {
  const data = await request<{ release: OtaRelease }>(
    `/api/admin/releases/${encodeURIComponent(version)}`,
    {
      method: "PATCH",
      body: JSON.stringify(patch),
    },
  );
  return data.release;
}

export async function deleteRelease(version: string): Promise<void> {
  await request<{ deleted: true; version: string }>(
    `/api/admin/releases/${encodeURIComponent(version)}`,
    { method: "DELETE" },
  );
}

export async function listDeviceOtaHistory(
  serial: string,
  limit = 20,
): Promise<OtaInstallLog[]> {
  const data = await request<{ history: OtaInstallLog[] }>(
    `/api/admin/devices/${encodeURIComponent(serial)}/ota-history?limit=${limit}`,
  );
  return data.history ?? [];
}

// ---------------------------------------------------------------------------
// Customers + Subscriptions — Phase 4
// ---------------------------------------------------------------------------

export async function createCustomer(
  inputs: CustomerCreateInputs,
): Promise<Customer> {
  const data = await request<{ customer: Customer }>(`/api/admin/customers`, {
    method: "POST",
    body: JSON.stringify(inputs),
  });
  return data.customer;
}

export async function getCustomer(
  id: string,
): Promise<{ customer: Customer; subscriptions: Subscription[] }> {
  const data = await request<{
    customer: Customer;
    subscriptions: Subscription[];
  }>(`/api/admin/customers/${encodeURIComponent(id)}`);
  return {
    customer: data.customer,
    subscriptions: data.subscriptions ?? [],
  };
}

export async function patchCustomer(
  id: string,
  patch: CustomerPatch,
): Promise<Customer> {
  const data = await request<{ customer: Customer }>(
    `/api/admin/customers/${encodeURIComponent(id)}`,
    {
      method: "PATCH",
      body: JSON.stringify(patch),
    },
  );
  return data.customer;
}

export async function createSubscription(
  customerId: string,
  inputs: SubscriptionCreateInputs,
): Promise<Subscription> {
  const data = await request<{ subscription: Subscription }>(
    `/api/admin/customers/${encodeURIComponent(customerId)}/subscription`,
    {
      method: "POST",
      body: JSON.stringify(inputs),
    },
  );
  return data.subscription;
}

/**
 * Operator-granted complimentary (free) subscription. Goes through the
 * dedicated server endpoint that skips Stripe entirely. The server REQUIRES
 * a device_serial because the `subscription_active` gate lives on the
 * device row — granting comp without naming the target device wouldn't
 * activate anything.
 */
export async function createComplimentarySubscription(
  customerId: string,
  inputs: ComplimentarySubscriptionCreateInputs,
): Promise<Subscription> {
  const data = await request<{ subscription: Subscription }>(
    `/api/admin/customers/${encodeURIComponent(customerId)}/complimentary-subscription`,
    {
      method: "POST",
      body: JSON.stringify(inputs),
    },
  );
  return data.subscription;
}

export async function linkDevice(
  customerId: string,
  deviceSerial: string,
): Promise<Device> {
  const data = await request<{ device: Device }>(
    `/api/admin/customers/${encodeURIComponent(customerId)}/link-device`,
    {
      method: "POST",
      body: JSON.stringify({ device_serial: deviceSerial }),
    },
  );
  return data.device;
}

export async function cancelSubscription(
  customerId: string,
  subscriptionId: string,
): Promise<{ stripe_subscription_id: string; status: string }> {
  const data = await request<{
    cancelled: boolean;
    stripe_subscription_id: string;
    status: string;
  }>(
    `/api/admin/customers/${encodeURIComponent(customerId)}/subscription/${encodeURIComponent(subscriptionId)}`,
    { method: "DELETE" },
  );
  return {
    stripe_subscription_id: data.stripe_subscription_id,
    status: data.status,
  };
}

// ---------------------------------------------------------------------------
// SIM admin endpoints — Phase 4
// ---------------------------------------------------------------------------

export async function activateSim(
  serial: string,
): Promise<{ state: string; api_called: boolean; hologram_device_id: number | null }> {
  return request<{
    state: string;
    api_called: boolean;
    hologram_device_id: number | null;
  }>(`/api/admin/devices/${encodeURIComponent(serial)}/sim/activate`, {
    method: "POST",
  });
}

export async function pauseSim(
  serial: string,
): Promise<{ state: string; api_called: boolean; hologram_device_id: number | null }> {
  return request<{
    state: string;
    api_called: boolean;
    hologram_device_id: number | null;
  }>(`/api/admin/devices/${encodeURIComponent(serial)}/sim/pause`, {
    method: "POST",
  });
}

export async function getSimUsage(serial: string): Promise<SimUsage> {
  const data = await request<SimUsage>(
    `/api/admin/devices/${encodeURIComponent(serial)}/sim/usage`,
  );
  return data;
}

export async function listPendingSimActions(
  serial: string,
): Promise<PendingSimAction[]> {
  const data = await request<{ pending_sim_actions: PendingSimAction[] }>(
    `/api/admin/devices/${encodeURIComponent(serial)}/pending-sim-actions`,
  );
  return data.pending_sim_actions ?? [];
}

// ---------------------------------------------------------------------------
// Zello — Phase 8 (Wave 2 admin endpoints)
//
// Routes registered in backend/api/src/handlers/admin_zello.ts:
//   GET    /api/admin/customers/:id/zello
//   POST   /api/admin/customers/:id/zello/add-device
//   POST   /api/admin/customers/:id/zello/add-channel
//   POST   /api/admin/customers/:id/zello/resend-credentials/:user_id
//   DELETE /api/admin/customers/:id/zello/channels/:channel_id
//   DELETE /api/admin/customers/:id/zello/users/:user_id
//
// All mutations return the up-to-date CustomerZelloState (or the new row,
// for symmetry with the rest of this client) so the SPA can either snap to
// truth via setQueryData or invalidate the `["customer-zello", id]` key —
// we currently do the latter for simplicity.
// ---------------------------------------------------------------------------

export async function getCustomerZello(
  customerId: string,
): Promise<CustomerZelloState> {
  return request<CustomerZelloState>(
    `/api/admin/customers/${encodeURIComponent(customerId)}/zello`,
  );
}

export async function addZelloDevice(
  customerId: string,
  inputs: AddZelloDeviceInputs,
): Promise<{ user: ZelloUser }> {
  return request<{ user: ZelloUser }>(
    `/api/admin/customers/${encodeURIComponent(customerId)}/zello/add-device`,
    {
      method: "POST",
      body: JSON.stringify(inputs),
    },
  );
}

export async function addZelloChannel(
  customerId: string,
  inputs: AddZelloChannelInputs,
): Promise<{ channel: ZelloChannel }> {
  return request<{ channel: ZelloChannel }>(
    `/api/admin/customers/${encodeURIComponent(customerId)}/zello/add-channel`,
    {
      method: "POST",
      body: JSON.stringify(inputs),
    },
  );
}

export async function resendZelloCredentials(
  customerId: string,
  userId: string,
): Promise<{ ok: true; email_sent_at: string | null }> {
  return request<{ ok: true; email_sent_at: string | null }>(
    `/api/admin/customers/${encodeURIComponent(customerId)}/zello/resend-credentials/${encodeURIComponent(userId)}`,
    { method: "POST", body: JSON.stringify({}) },
  );
}

export async function deleteZelloChannel(
  customerId: string,
  channelId: string,
): Promise<{ deleted: true; id: string }> {
  return request<{ deleted: true; id: string }>(
    `/api/admin/customers/${encodeURIComponent(customerId)}/zello/channels/${encodeURIComponent(channelId)}`,
    { method: "DELETE" },
  );
}

export async function deleteZelloUser(
  customerId: string,
  userId: string,
): Promise<{ deleted: true; id: string }> {
  return request<{ deleted: true; id: string }>(
    `/api/admin/customers/${encodeURIComponent(customerId)}/zello/users/${encodeURIComponent(userId)}`,
    { method: "DELETE" },
  );
}

// ---------------------------------------------------------------------------
// RallyStream appliances (docs/RALLYSTREAM_FLEET_INTEGRATION.md)
// ---------------------------------------------------------------------------

export async function listRallystreamDevices(): Promise<RallyStreamDevice[]> {
  const data = await request<{ devices: RallyStreamDevice[] }>(
    "/api/admin/rs/devices",
  );
  return data.devices ?? [];
}

export async function getRallystreamDevice(
  publicId: string,
): Promise<RallyStreamDevice> {
  const data = await request<{ device: RallyStreamDevice }>(
    `/api/admin/rs/devices/${encodeURIComponent(publicId)}`,
  );
  return data.device;
}

export async function createRallystreamDevice(
  inputs: RallyStreamDeviceCreateInputs,
): Promise<RallyStreamDevice> {
  const data = await request<{ device: RallyStreamDevice }>(
    "/api/admin/rs/devices",
    {
      method: "POST",
      body: JSON.stringify(inputs),
    },
  );
  return data.device;
}
